import { useState } from 'react';

type Props = {
  src: string;
  alt: string;
  className?: string;
  /** Applied to the <img> (e.g. larger max-height in split-flow left rail) */
  imgClassName?: string;
  /** Inline SVG fallback shown when the PNG asset isn't present yet */
  fallbackSvg?: React.ReactNode;
};

/**
 * Cover art for split-flow left panel.
 * Shows the PNG when available; falls back to `fallbackSvg` (or a generic placeholder) otherwise.
 */
export function FlowCoverImage({ src, alt, className = '', imgClassName = '', fallbackSvg }: Props) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    if (fallbackSvg) {
      return (
        <div className={`mx-auto w-full flex items-center justify-center ${className}`} aria-hidden>
          {fallbackSvg}
        </div>
      );
    }
    return (
      <div className={`mx-auto w-full flex items-center justify-center py-4 ${className}`} aria-hidden>
        <GenericOnboardingIllustration />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`mx-auto w-full object-contain drop-shadow-xl ${imgClassName} ${className}`}
      style={{ maxHeight: '100%', width: '100%' }}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

/** Beautiful inline SVG used when no PNG asset exists yet */
function GenericOnboardingIllustration() {
  return (
    <svg
      viewBox="0 0 320 320"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full"
      style={{ maxWidth: '100%' }}
      aria-hidden
    >
      {/* outer glow ring */}
      <circle cx="160" cy="150" r="130" fill="rgba(255,255,255,0.04)" />
      <circle cx="160" cy="150" r="100" fill="rgba(255,255,255,0.06)" />

      {/* main card */}
      <rect x="28" y="48" width="264" height="220" rx="22" fill="rgba(255,255,255,0.13)" />
      <rect x="28" y="48" width="264" height="220" rx="22" stroke="rgba(255,255,255,0.24)" strokeWidth="1.5" />

      {/* card header strip */}
      <rect x="28" y="48" width="264" height="56" rx="22" fill="rgba(255,255,255,0.18)" />
      <rect x="28" y="86" width="264" height="18" fill="rgba(255,255,255,0.18)" />

      {/* avatar */}
      <circle cx="66" cy="76" r="18" fill="rgba(255,255,255,0.88)" />
      <circle cx="66" cy="70" r="8" fill="rgba(139,128,249,0.75)" />
      <path d="M52 88c0-8 6.3-13 14-13s14 5 14 13" fill="rgba(139,128,249,0.55)" />

      {/* name + role lines */}
      <rect x="96" y="67" width="92" height="9" rx="4.5" fill="rgba(255,255,255,0.82)" />
      <rect x="96" y="82" width="60" height="7" rx="3.5" fill="rgba(255,255,255,0.45)" />

      {/* status pill */}
      <rect x="228" y="65" width="52" height="20" rx="10" fill="rgba(139,128,249,0.65)" />
      <rect x="235" y="71.5" width="38" height="7" rx="3.5" fill="rgba(255,255,255,0.92)" />

      {/* attribute rows */}
      {[122, 142, 162, 182, 202].map((y, i) => (
        <g key={y}>
          <rect x="44" y={y} width={i % 2 === 0 ? 110 : 90} height="9" rx="4.5"
            fill="rgba(255,255,255,0.58)" />
          <rect x={44 + (i % 2 === 0 ? 122 : 102)} y={y}
            width={i % 2 === 0 ? 70 : 88} height="9" rx="4.5"
            fill="rgba(255,255,255,0.32)" />
        </g>
      ))}

      {/* CTA button */}
      <rect x="44" y="232" width="232" height="30" rx="12" fill="rgba(255,255,255,0.92)" />
      <rect x="100" y="242" width="120" height="10" rx="5" fill="rgba(107,84,212,0.72)" />

      {/* floating checkmark badge (top-right) */}
      <circle cx="258" cy="48" r="26" fill="rgba(139,128,249,0.95)" />
      <path d="M248 48l7 8 13-16" stroke="white" strokeWidth="3" strokeLinecap="round"
        strokeLinejoin="round" />

      {/* progress dots (bottom) */}
      {[0, 1, 2, 3, 4].map((i) => (
        <circle key={i} cx={130 + i * 15} cy="290" r={i === 0 ? 6 : 4}
          fill={i === 0 ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.28)'} />
      ))}
    </svg>
  );
}
